import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

interface Journal {
  id: string;
  content: string;
  updated_at: string;
  sentiment: number | null;
  emotions: string[] | null;
}

function Dashboard() {
  const { currentUser, fetchUserStats } = useAuth();
  const [journals, setJournals] = useState<Journal[]>([]);
  const [newJournal, setNewJournal] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingJournalId, setEditingJournalId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [affirmation, setAffirmation] = useState('');
  const [isFetchingAffirmation, setIsFetchingAffirmation] = useState(false);
  const [selectedEmotion, setSelectedEmotion] = useState<number | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [promptKeywords, setPromptKeywords] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [showPromptGenerator, setShowPromptGenerator] = useState(false);

  const fetchAffirmation = async () => {
    setIsFetchingAffirmation(true);
    try {
      const response = await fetch('/api/affirmation');
      const data = await response.json();
      setAffirmation(data.affirmation);
    } catch (error) {
      console.error('Failed to fetch affirmation:', error);
      setAffirmation('You are capable of amazing things.'); // Fallback affirmation
    } finally {
      setIsFetchingAffirmation(false);
    }
  };

  useEffect(() => {
    const fetchJournals = async () => {
      if (!currentUser) return;

      setLoading(true);
      const { data, error } = await supabase
        .from('journals')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('updated_at', { ascending: false });

      if (error) {
        setError(error.message);
      } else {
        setJournals(data || []);
      }
      setLoading(false);
    };

    fetchJournals();
    fetchAffirmation();
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newJournal.trim()) return;

    try {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newJournal }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze journal entry');
      }

      const analysis = await response.json();

      const { data, error: supabaseError } = await supabase
        .from('journals')
        .insert([
          {
            content: newJournal,
            user_id: currentUser.id,
            sentiment: analysis.sentiment,
            emotions: selectedEmotion,
          },
        ])
        .select();

      if (supabaseError) throw supabaseError;

      if (data) {
        setJournals([data[0], ...journals]);
        setNewJournal('');
        setSelectedEmotion(null);
        setError(null);
        setFollowUpQuestion(analysis.followUpQuestion);
        fetchUserStats();
        fetchAffirmation();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const { error } = await supabase.from('journals').delete().eq('id', id);

      if (error) {
        setError(error.message);
      } else {
        setJournals(journals.filter((j) => j.id !== id));
        setError(null);
        fetchUserStats();
      }
    }
  };

  const handleUpdate = async (id:string) => {
    if (!editedContent.trim()) return;

    try {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: editedContent }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze journal entry');
      }

      const analysis = await response.json();

      const { data, error: supabaseError } = await supabase
        .from('journals')
        .update({
          content: editedContent,
          sentiment: analysis.sentiment,
          emotions: selectedEmotion,
        })
        .eq('id', id)
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      if (data) {
        setJournals([data, ...journals.filter((j) => j.id !== id)]);
        setEditingJournalId(null);
        setEditedContent('');
        setSelectedEmotion(null);
        setError(null);
        setFollowUpQuestion(analysis.followUpQuestion);
        fetchUserStats();
        fetchAffirmation();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleGeneratePrompt = async (isRegenerating = false) => {
    if (!promptKeywords.trim()) {
      setPromptError('Please enter some keywords to generate a prompt.');
      return;
    }

    setIsGeneratingPrompt(true);
    setPromptError(null);
    if (!isRegenerating) {
      setGeneratedPrompt('');
    }

    try {
      const response = await fetch('/api/generate-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywords: promptKeywords }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate prompt.');
      }

      const data = await response.json();
      setGeneratedPrompt(data.prompt);
      setShowPromptGenerator(true);
    } catch (err: any) {
      setPromptError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const startEditing = (journal: Journal) => {
    setEditingJournalId(journal.id);
    setEditedContent(journal.content);
  };

  const cancelEditing = () => {
    setEditingJournalId(null);
    setEditedContent('');
  };


  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = journals.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(journals.length / entriesPerPage);

  return (
    <main className="main-content dashboard-page">
      <div className="dashboard-container">
        <div className="affirmation-container">
          <p className="affirmation-text">{affirmation}</p>
          <button type="button" onClick={fetchAffirmation} className="refresh-btn" disabled={isFetchingAffirmation}>
            &#x21bb;
          </button>
        </div>
        <h2>Your Journal</h2>
        <div className="journal-prompt-header">
                    <div className="journal-subtitle">How are you feeling today?</div>
          <div className="emotion-selector">
            <button type="button" className={`emotion-icon ${selectedEmotion === 5 ? 'selected' : ''}`} onClick={() => setSelectedEmotion(5)}>😄</button>
            <button type="button" className={`emotion-icon ${selectedEmotion === 4 ? 'selected' : ''}`} onClick={() => setSelectedEmotion(4)}>🙂</button>
            <button type="button" className={`emotion-icon ${selectedEmotion === 3 ? 'selected' : ''}`} onClick={() => setSelectedEmotion(3)}>😐</button>
            <button type="button" className={`emotion-icon ${selectedEmotion === 2 ? 'selected' : ''}`} onClick={() => setSelectedEmotion(2)}>☹️</button>
            <button type="button" className={`emotion-icon ${selectedEmotion === 1 ? 'selected' : ''}`} onClick={() => setSelectedEmotion(1)}>😢</button>
          </div>
        </div>
        <div className="prompt-generator-container">
          {!showPromptGenerator ? (
            <div className="prompt-input-view">
              <input
                type="text"
                value={promptKeywords}
                onChange={(e) => setPromptKeywords(e.target.value)}
                placeholder="Need inspiration? Enter a keyword, e.g., 'gratitude' or 'a challenge I faced'"
                className="prompt-keywords-input"
              />
              <button onClick={() => handleGeneratePrompt()} className="btn btn-secondary prompt-generator-btn" disabled={isGeneratingPrompt}>
                {isGeneratingPrompt ? '...' : 'Prompt'}
              </button>
            </div>
          ) : (
            <div className="prompt-display-view">
              <p className="generated-prompt-text">{generatedPrompt}</p>
              <div className="prompt-actions">
                <button onClick={() => {
                  setGeneratedPrompt('');
                  setShowPromptGenerator(false);
                  setPromptKeywords('');
                }} className="btn-icon">&#x2190;</button> {/* Back arrow */}
                <button onClick={() => handleGeneratePrompt(true)} className={`btn-icon refresh-icon ${isGeneratingPrompt ? 'spinning' : ''}`} disabled={isGeneratingPrompt}>&#x21bb;</button> {/* Refresh icon */}
              </div>
            </div>
          )}
          {promptError && <p className="error-message">{promptError}</p>}
        </div>

        <form onSubmit={handleSubmit} className="journal-form">
          <textarea
            value={newJournal}
            onChange={(e) => setNewJournal(e.target.value)}
            placeholder="How are you doing?"
            rows={4}
            required
          ></textarea>
          <button type="submit" className="btn btn-confirm btn-submit">Submit</button>
        </form>

        {error && <p className="error-message">{error}</p>}

        {followUpQuestion && (
          <div className="follow-up-question">
            <p>{followUpQuestion}</p>
            <button onClick={() => setFollowUpQuestion(null)} className="btn-icon btn-close">&times;</button>
          </div>
        )}

        <div className="journals-list">
          {loading ? (
            <p>Loading journals...</p>
          ) : (
            currentEntries.map((journal) => (
              <div key={journal.id} className="journal-entry">
                {editingJournalId === journal.id ? (
                  <div className="edit-mode">
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      rows={4}
                    />
                    <div className="edit-actions">
                      <button onClick={() => handleUpdate(journal.id)} className="btn btn-confirm">Save</button>
                      <button onClick={cancelEditing} className="btn btn-secondary btn-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="view-mode">
                    <p>{journal.content}</p>
                    <div className="entry-meta">
                      <span>Last updated: {new Date(journal.updated_at).toLocaleString()}</span>
                      {journal.sentiment !== null && (
                        <span className={`sentiment-score sentiment-${getSentimentLabel(journal.sentiment)}`}>
                          Sentiment: {journal.sentiment.toFixed(2)}
                        </span>
                      )}
                      <div className="entry-actions">
                        <button onClick={() => startEditing(journal)} className="btn-icon btn-confirm">Edit</button>
                        <button onClick={() => handleDelete(journal.id)} className="btn-icon btn-danger">Delete</button>
                      </div>
                    </div>
                    {journal.emotions && journal.emotions.length > 0 && (
                      <div className="emotions-container">
                        {journal.emotions.map((emotion) => (
                          <span key={emotion} className={`emotion-tag emotion-${emotion}`}>
                            {emotion}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {journals.length > 0 && (
          <div className="pagination-controls">
            <div className="entries-per-page">
              <label htmlFor="entries-per-page">Entries per page:</label>
              <select
                id="entries-per-page"
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page
                }}
              >
                <option value={3}>3</option>
                <option value={5}>5</option>
                <option value={10}>10</option>
              </select>
            </div>
            <div className="page-navigation">
              <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                &lt; Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
                Next &gt;
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}


const getSentimentLabel = (score: number): string => {
  if (score > 0.05) return 'positive';
  if (score < -0.05) return 'negative';
  return 'neutral';
};

export default Dashboard;
